import { MatchStatus, resolveEnum } from '@/models/tournament';

/**
 * CO-921 (owner decision 2026-10-21): once a match has been PLAYED (Completed or UnderRevision) its
 * roster, work-point overrides, format, type and date decide who is paid what, so only HR may change
 * them, or delete the match. Mirrors `PostCompletionGuard.IsPlayed` on the server, which is the gate;
 * the client only keeps its buttons from promising an action the server will refuse with a 403
 * (`TOURNAMENT_POSTCOMPLETION_HR_ONLY`). A Closed match is locked for everyone until it is reopened.
 */
export const normaliseMatchStatus = (status: MatchStatus | string | number | undefined | null): MatchStatus =>
  (resolveEnum(MatchStatus, status) as MatchStatus) ?? MatchStatus.Scheduled;

export const isPlayedMatchStatus = (status: MatchStatus | string | number | undefined | null): boolean => {
  const s = normaliseMatchStatus(status);
  return s === MatchStatus.Completed || s === MatchStatus.UnderRevision;
};

export const isClosedMatchStatus = (status: MatchStatus | string | number | undefined | null): boolean =>
  normaliseMatchStatus(status) === MatchStatus.Closed;

/** Server error code for an Admin refused on a played match. */
export const POSTCOMPLETION_HR_ONLY_CODE = 'TOURNAMENT_POSTCOMPLETION_HR_ONLY';

/**
 * Parses a `WorkPointsReconciled` history reason ("14 -> 0 pts: replaced on a completed match").
 * Returns null when the text has another shape, so the caller can fall back to showing nothing.
 */
export const parseReconciledReason = (
  reason: string | null | undefined,
): { from: number; to: number; trigger: string } | null => {
  if (!reason) return null;
  const m = /^\s*(-?\d+(?:\.\d+)?)\s*->\s*(-?\d+(?:\.\d+)?)\s*pts:\s*(.*)$/i.exec(reason);
  if (!m) return null;
  return { from: Number(m[1]), to: Number(m[2]), trigger: m[3].trim() };
};

/**
 * The server writes the reconcile trigger in English (see the `ReconcileMatchAsync` callers). Map the
 * known ones to translation keys so the history never renders server English to an Arabic reader; an
 * unknown trigger maps to null and the UI shows only the points change.
 */
export const reconcileTriggerKey = (trigger: string): string | null => {
  const t = trigger.toLowerCase();
  if (t.startsWith('replaced on a completed match')) return 'wpFollow.trigger.replaced';
  if (t.startsWith('officials changed on a completed match')) return 'wpFollow.trigger.officialsChanged';
  if (t.startsWith('format, type or date corrected')) return 'wpFollow.trigger.pricingChanged';
  if (t.startsWith('officials or overrides changed')) return 'wpFollow.trigger.rosterEdited';
  if (t.startsWith('match deleted')) return 'wpFollow.trigger.deleted';
  if (t.startsWith('results updated')) return 'wpFollow.trigger.resultsUpdated';
  if (t.startsWith('officials changed by bulk import')) return 'wpFollow.trigger.bulkImport';
  if (t.startsWith('work points recalculated')) return 'wpFollow.trigger.recalculated';
  // "match 12 (A vs B, 2026-10-03) cancelled" (UpdateMatchStatus -> Cancelled on a played match).
  if (t.startsWith('match ') && t.endsWith(' cancelled')) return 'wpFollow.trigger.cancelled';
  return null;
};
